// JSON value produced by MikeBurton.Shared.Enums.AccommodationType.CampsiteTent
const CAMPSITE_TENT_TYPE = 'campsiteTent';

// ─────────────────────────────────────────────────────────────
// Core rule-matching (ported from docs/camping/completed/validation-logic.ts)
// ─────────────────────────────────────────────────────────────

function doesAllocationMatchRule(allocation, totalOccupants, rule) {
  if (totalOccupants < rule.minimumPartySize) return false;
  if (totalOccupants > rule.maximumPartySize) return false;

  const constraintsSatisfied = rule.paxRules.every((constraint) => {
    const count = allocation[constraint.paxTypeKey] ?? 0;
    return count >= constraint.minimumPaxCount && count <= constraint.maximumPaxCount;
  });

  if (!constraintsSatisfied) return false;

  // Pax types not referenced by any constraint are implicitly disallowed
  const allowedKeys = new Set(rule.paxRules.map((c) => c.paxTypeKey));
  const hasUnexpectedTypes = Object.entries(allocation).some(
    ([key, count]) => count > 0 && !allowedKeys.has(key)
  );

  return !hasUnexpectedTypes;
}

function validateTentAllocation(allocation, tentSubItem) {
  const errors = [];
  const totalOccupants = Object.values(allocation).reduce((sum, c) => sum + c, 0);

  // TENT_EMPTY is unreachable through validateCampingAllocation: buildTentAllocations only
  // creates an entry when at least one member is assigned, so totalOccupants is always >= 1.
  // Unoccupied tent instances are caught upstream as PAX_NOT_FULLY_ALLOCATED instead.
  if (totalOccupants === 0) {
    errors.push({ code: 'TENT_EMPTY' });
    return { isValid: false, errors };
  }

  if (totalOccupants > tentSubItem.numberOfBerths) {
    errors.push({ code: 'TENT_OVER_CAPACITY' });
    return { isValid: false, errors };
  }

  const ruleSets = tentSubItem.paxOccupancyRuleSets ?? [];
  const matchesRule = ruleSets.some((rule) =>
    doesAllocationMatchRule(allocation, totalOccupants, rule)
  );

  if (!matchesRule) {
    errors.push({ code: 'TENT_NO_MATCHING_RULE' });
  }

  return { isValid: errors.length === 0, errors };
}

function validateAllocationCompleteness(selectedPaxTypes, tentAllocations) {
  const totalsAllocated = tentAllocations.reduce((acc, tent) => {
    Object.entries(tent.allocatedPax).forEach(([paxTypeKey, count]) => {
      acc[paxTypeKey] = (acc[paxTypeKey] ?? 0) + count;
    });
    return acc;
  }, {});

  const errors = Object.entries(selectedPaxTypes).flatMap(([paxTypeKey, requiredCount]) => {
    if (requiredCount === 0) return [];
    const allocatedCount = totalsAllocated[paxTypeKey] ?? 0;
    if (allocatedCount < requiredCount) return [{ code: 'PAX_NOT_FULLY_ALLOCATED', paxTypeKey }];
    // PAX_OVER_ALLOCATED is unreachable through validateCampingAllocation: both
    // buildSelectedPaxTypes and buildTentAllocations count exactly once per member per
    // paxTypeKey from the same partyMembers array, so allocatedCount <= requiredCount always.
    if (allocatedCount > requiredCount) return [{ code: 'PAX_OVER_ALLOCATED', paxTypeKey }];
    return [];
  });

  return { isValid: errors.length === 0, errors };
}

// ─────────────────────────────────────────────────────────────
// Public API
// ─────────────────────────────────────────────────────────────

/**
 * Builds tent-level allocations from the current partyMembers state.
 * Each partyMember's accommodationSubItemAllocations.camping.roomAllocation.value = { key: tentSubItemKey, index }.
 * Multiple members can share the same (key, index) pair.
 *
 * Returns an array of { tentSubItemKey, instanceIndex, allocatedPax }
 * where allocatedPax is Record<paxTypeKey, count>.
 */
function buildTentAllocations(partyMembers) {
  const map = partyMembers.reduce((acc, pm) => {
    const allocation = pm.accommodationSubItemAllocations?.camping?.roomAllocation?.value;
    if (!allocation || allocation.index < 0) return acc;

    const { key, index } = allocation;
    const paxTypeKey = pm.paxKey?.value;
    if (!paxTypeKey) return acc;

    const mapKey = `${key},${index}`;
    const existing = acc[mapKey] ?? { tentSubItemKey: key, instanceIndex: index, allocatedPax: {} };
    return {
      ...acc,
      [mapKey]: {
        ...existing,
        allocatedPax: {
          ...existing.allocatedPax,
          [paxTypeKey]: (existing.allocatedPax[paxTypeKey] ?? 0) + 1,
        },
      },
    };
  }, {});

  return Object.values(map);
}

/**
 * Derives the expected pax count per type from the party members list.
 */
function buildSelectedPaxTypes(partyMembers) {
  return partyMembers.reduce((acc, pm) => {
    const key = pm.paxKey?.value;
    if (!key) return acc;
    return { ...acc, [key]: (acc[key] ?? 0) + 1 };
  }, {});
}

/**
 * Validates the current tent allocation against the paxOccupancyRuleSets on
 * each CampsiteTentSubItem. Skips non-tent sub-items.
 *
 * Returns { isValid, errors } where each error has:
 *   code            — one of the ValidationErrorCode values
 *   tentSubItemKey  — which tent sub-item the error relates to (tent-level errors)
 *   instanceIndex   — which instance (0-based) of that tent type
 *   paxTypeKey      — which pax type (completeness errors)
 */
export function validateCampingAllocation(partyMembers, accommodationSubItems) {
  const tentSubItems = (accommodationSubItems ?? []).filter(
    (s) => s.accommodationType === CAMPSITE_TENT_TYPE
  );

  if (tentSubItems.length === 0) return { isValid: true, errors: [] };

  const tentAllocations = buildTentAllocations(partyMembers);
  const selectedPaxTypes = buildSelectedPaxTypes(partyMembers);

  const completenessErrors = validateAllocationCompleteness(
    selectedPaxTypes,
    tentAllocations
  ).errors;

  const tentErrors = tentAllocations.flatMap(({ tentSubItemKey, instanceIndex, allocatedPax }) => {
    const tentSubItem = tentSubItems.find((s) => s.key === tentSubItemKey);
    if (!tentSubItem) return [];
    return validateTentAllocation(allocatedPax, tentSubItem).errors.map((e) => ({
      ...e,
      tentSubItemKey,
      instanceIndex,
    }));
  });

  const errors = [...completenessErrors, ...tentErrors];
  return { isValid: errors.length === 0, errors };
}

/**
 * Maps campingValidation errors to label-keyed message strings and joins with <br/>.
 * Falls back gracefully when a label is missing.
 */
export function formatCampingErrors(errors, labels) {
  const codeToLabelKey = {
    TENT_EMPTY: 'missingTentAllocationValidationMessage',
    TENT_OVER_CAPACITY: 'tentOccupancyAllocationInvalidValidationMessage',
    TENT_NO_MATCHING_RULE: 'tentOccupancyAllocationInvalidValidationMessage',
    PAX_NOT_FULLY_ALLOCATED: 'missingTentAllocationValidationMessage',
    PAX_OVER_ALLOCATED: 'tentOccupancyAllocationInvalidValidationMessage',
  };

  const seen = new Set();
  const messages = errors.reduce((acc, err) => {
    const key = codeToLabelKey[err.code];
    if (!key || seen.has(key)) return acc;
    seen.add(key);
    const message = labels?.[key];
    if (!message) return acc;
    return [...acc, message];
  }, []);

  return messages.length > 0 ? messages.join('<br/>') : null;
}
